<template>
  <div
    class="app-tabs flex items-center gap-0.5"
    :style="{
      borderBottom: bordered ? '1px solid var(--line)' : 'none',
      paddingLeft: pad ? '12px' : '0',
      paddingRight: pad ? '12px' : '0',
    }"
  >
    <button
      v-for="tab in tabs"
      :key="tab.value"
      type="button"
      class="app-tab inline-flex items-center gap-1.5"
      :style="tabStyle(tab)"
      @click="select(tab)"
    >
      <UIcon v-if="tab.icon" :name="tab.icon" class="h-4 w-4" />
      <span>{{ tab.label }}</span>
      <span
        v-if="tab.count != null"
        class="app-tab-count"
        :style="{
          fontSize: '12px',
          padding: '0 6px',
          borderRadius: '999px',
          background: isActive(tab) ? 'var(--accent-soft, var(--raise))' : 'var(--raise)',
          color: isActive(tab) ? 'var(--acctext)' : 'var(--faint)',
        }"
      >{{ tab.count }}</span>
    </button>

    <div class="flex-1" />
    <slot name="right" />
  </div>
</template>

<script setup lang="ts">
export interface AppTab {
  label: string
  value: string
  icon?: string
  count?: number | null
  /** If provided, clicking navigates here instead of emitting update:modelValue. */
  to?: string
}

const props = withDefaults(
  defineProps<{
    tabs: AppTab[]
    modelValue?: string
    /** show the bottom divider line under the tab row */
    bordered?: boolean
    /** apply horizontal padding to align with card gutters */
    pad?: boolean
    /** compact = smaller font/padding, for nested sub-navs */
    size?: 'md' | 'sm'
  }>(),
  { bordered: true, pad: false, size: 'md' },
)

const emit = defineEmits<{ 'update:modelValue': [string] }>()

function isActive(tab: AppTab) {
  return tab.value === props.modelValue
}

function select(tab: AppTab) {
  if (tab.to) {
    navigateTo(tab.to)
    return
  }
  emit('update:modelValue', tab.value)
}

function tabStyle(tab: AppTab) {
  const active = isActive(tab)
  const sm = props.size === 'sm'
  return {
    height: sm ? '34px' : '40px',
    padding: sm ? '0 10px' : '0 12px',
    fontSize: sm ? '13px' : '15px',
    fontWeight: 600,
    color: active ? 'var(--ink)' : 'var(--muted)',
    boxShadow: active ? 'inset 0 -2px 0 var(--acctext)' : 'none',
    cursor: 'pointer',
    background: 'transparent',
    border: 'none',
    transition: 'color 0.12s ease',
  }
}
</script>

<style scoped>
.app-tab:hover {
  color: var(--ink) !important;
}
/* On a narrow screen the row scrolls instead of widening the page. */
.app-tabs {
  overflow-x: auto;
}
.app-tab {
  flex-shrink: 0;
  white-space: nowrap;
}
</style>
